import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Target, Sparkles, DollarSign, TrendingDown, Settings2,
  Play, RefreshCw, Download, CheckCircle, AlertTriangle, Upload, FileUp,
  ChevronDown, Handshake, UserPlus, Trophy, SkipForward, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, AreaChart, Area,
} from 'recharts';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';
import InfoTip from '@/components/InfoTip';
import api from '@/services/api';
import QuickAssignModal from '@/components/QuickAssignModal';

/* Quick Actions dropdown — consistent with DesignCenter / BidValidator */
const QuickActions = ({ mpn, onAssign }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" data-testid={`quick-action-${mpn}`}>
        Action <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')}>
        <Handshake className="h-3.5 w-3.5 mr-2 text-green-600" /> Refer to 1Source
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAssign?.()}>
        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Report Win opened')}>
        <Trophy className="h-3.5 w-3.5 mr-2 text-amber-600" /> Report Win
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Skipped')}>
        <SkipForward className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/* Generate trend data for a component */
function genTrend(base, pctChange) {
  return Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    price: base + (base * pctChange / 100) * (i / 30) + (Math.random() - 0.5) * base * 0.03,
  }));
}

const CostArchitect = () => {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const { branding } = useBranding();
  const [targetCost, setTargetCost] = useState(100000);
  const [currentCost, setCurrentCost] = useState(0);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [boms, setBoms] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [constraints, setConstraints] = useState({
    riskTolerance: 50,
    distributorType: 'authorized',
    alternateType: 'fff',
  });

  useEffect(() => { fetchBoms(); }, []);

  const fetchBoms = async () => {
    try {
      const res = await api.get('/bom/list');
      setBoms(res.data.boms || []);
    } catch { /* empty list ok */ }
  };

  const selectBom = (bomId) => {
    const bom = boms.find(b => b.bom_id === bomId);
    if (bom) {
      setSelectedBom(bom);
      setCurrentCost(bom.total_value || 125000);
      setTargetCost(Math.round((bom.total_value || 125000) * 0.85));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/bom/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded: ${res.data.message}`);
      await fetchBoms();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const runSimulation = () => {
    if (!selectedBom) {
      toast.error('Please select or upload a BOM first');
      return;
    }
    setIsSimulating(true);
    setTimeout(() => {
      setSimulationResult({
        achievable: true,
        optimizedCost: targetCost * 1.02,
        savings: currentCost - (targetCost * 1.02),
        confidence: 87,
        matchedParts: selectedBom.total_components ? Math.max(Math.round(selectedBom.total_components * 0.82), 1) : 5,
        totalParts: selectedBom.total_components || 6,
        strategies: [
          { name: 'FFF Alternates', impact: 8500, components: 12 },
          { name: 'Global Sourcing', impact: 5200, components: 8 },
          { name: 'Volume Optimization', impact: 4300, components: 15 },
          { name: 'Timing Optimization', impact: 3000, components: 6 },
          { name: 'Negotiation Insights', impact: 2000, components: 4 },
        ],
        componentPath: [
          { mpn: 'STM32F407VGT6', current: 12.50, bestRate: 9.20, negotiationAnchor: 10.80, optimized: 9.80, strategy: 'Switch to GD32F407 (FFF)', savings: 5400, risk: 'Low', trend: 5, trendData: genTrend(12.50, 5) },
          { mpn: 'GRM188R71H104KA93D', current: 0.028, bestRate: 0.012, negotiationAnchor: 0.018, optimized: 0.015, strategy: 'Consolidate volume + switch supplier', savings: 3250, risk: 'Low', trend: -3, trendData: genTrend(0.028, -3) },
          { mpn: 'MT48LC16M16A2', current: 3.50, bestRate: 2.10, negotiationAnchor: 2.80, optimized: 2.45, strategy: 'Independent distributor + quality test', savings: 2625, risk: 'Medium', trend: 18, trendData: genTrend(3.50, 18) },
          { mpn: 'ESP32-WROOM-32E', current: 3.20, bestRate: 2.00, negotiationAnchor: 2.60, optimized: 2.35, strategy: 'Direct from Espressif (volume)', savings: 2550, risk: 'Low', trend: 0, trendData: genTrend(3.20, 0) },
          { mpn: 'TPS54331DR', current: 1.65, bestRate: 1.05, negotiationAnchor: 1.35, optimized: 1.20, strategy: 'Global sourcing (Asia)', savings: 1350, risk: 'Low', trend: -5, trendData: genTrend(1.65, -5) },
          { mpn: 'USB-C-16P-SMD', current: 0.38, bestRate: 0.22, negotiationAnchor: 0.30, optimized: 0.28, strategy: 'Negotiate with current supplier', savings: 500, risk: 'Low', trend: 12, trendData: genTrend(0.38, 12) },
        ],
        supplyChainRisks: [
          { component: 'MT48LC16M16A2', level: 'Medium', detail: 'Concentrated supplier base in Taiwan. 2 of 3 sources in same region.', mitigation: 'Qualify W9825G6KH-6 as alternate from different geography.' },
          { component: 'STM32F407VGT6', level: 'Low', detail: 'ST Micro has 3 fab sites. Lead times stable at 12 weeks.', mitigation: 'Monitor quarterly. GD32F407 pre-qualified as backup.' },
          { component: 'USB-C-16P-SMD', level: 'Low', detail: 'Commodity connector, 8+ sources globally.', mitigation: 'No immediate action. Standard multi-source.' },
          { component: 'ESP32-WROOM-32E', level: 'Low', detail: 'Espressif sole source but high inventory across distributors.', mitigation: 'Maintain 8-week buffer stock.' },
          { component: 'GRM188R71H104KA93D', level: 'Low', detail: 'MLCC supply normalized post-shortage. Multiple vendors.', mitigation: 'Continue volume consolidation strategy.' },
          { component: 'TPS54331DR', level: 'Low', detail: 'TI broad availability. No constraints.', mitigation: 'None needed.' },
        ],
      });
      setIsSimulating(false);
      toast.success('Simulation complete!');
    }, 2000);
  };

  const COLORS = ['#16a34a', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ec4899'];

  const exportPDF = () => {
    if (!simulationResult) return;
    const doc = createBrandedPDF('Cost Architect', 'AI-Powered Cost Simulation Results', branding);
    let y = doc._startY;
    y = addSectionTitle(doc, 'Simulation Summary', y);
    y = addMetricCards(doc, [
      { label: 'Current Cost', value: `$${currentCost.toLocaleString()}` },
      { label: 'Target Cost', value: `$${targetCost.toLocaleString()}` },
      { label: 'Optimized Cost', value: `$${simulationResult.optimizedCost.toLocaleString()}` },
      { label: 'Matched Parts', value: `${simulationResult.matchedParts}/${simulationResult.totalParts}` },
    ], y);
    y = addSectionTitle(doc, 'Component Optimization Path', y);
    y = addTable(doc, ['MPN', 'Current', 'Best Rate', 'Anchor', 'Optimized', 'Strategy', 'Savings', 'Risk'],
      simulationResult.componentPath.map(c => [c.mpn, `$${c.current.toFixed(3)}`, `$${c.bestRate.toFixed(3)}`, `$${c.negotiationAnchor.toFixed(3)}`, `$${c.optimized.toFixed(3)}`, c.strategy, `$${c.savings.toLocaleString()}`, c.risk]), y);
    downloadPDF(doc, 'cost-architect-simulation.pdf');
    toast.success('PDF downloaded');
  };

  return (
    <Layout title="Cost Architect" subtitle="AI-powered price simulation to hit your target BOM cost">
      <div className="space-y-6" data-testid="cost-architect-page">
        {/* BOM Selection / Upload */}
        <Card className={`shadow-sm border-2 ${selectedBom ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`} data-testid="bom-upload-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {selectedBom ? 'BOM Selected' : 'Select or Upload a BOM to Begin'}
              <InfoTip text="A BOM or component list is required to run the cost simulation. Upload a new file or select an existing BOM." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <Select value={selectedBom?.bom_id || ''} onValueChange={selectBom}>
                <SelectTrigger className="w-64 bg-white" data-testid="ca-bom-select">
                  <SelectValue placeholder="Select existing BOM..." />
                </SelectTrigger>
                <SelectContent>
                  {boms.map(b => (
                    <SelectItem key={b.bom_id} value={b.bom_id}>
                      {b.name} ({b.total_components} parts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">or</span>
              <label className="cursor-pointer">
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="ca-file-input" />
                <Button variant="outline" className="border-blue-300 text-blue-700" asChild disabled={uploading}>
                  <span>
                    <FileUp className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload BOM File'}
                  </span>
                </Button>
              </label>
              {selectedBom && (
                <div className="ml-auto flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    {selectedBom.name} — ${selectedBom.total_value?.toLocaleString()} | {selectedBom.total_components} components
                  </span>
                </div>
              )}
            </div>
            {!selectedBom && (
              <p className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> You must select or upload a BOM before running a simulation.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Config Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-green-800 flex items-center gap-2"><Target className="h-5 w-5" /> Target Cost</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input type="number" value={targetCost} onChange={e => setTargetCost(Number(e.target.value))} className="pl-10 text-2xl font-bold h-14 bg-white" data-testid="target-cost-input" />
              </div>
              <p className="text-sm text-gray-600">Current BOM Cost: <span className="font-bold text-gray-900">${currentCost.toLocaleString()}</span></p>
              {currentCost > 0 && <p className="text-sm text-green-700 font-medium">Target Reduction: {((1 - targetCost / currentCost) * 100).toFixed(1)}%</p>}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><Settings2 className="h-5 w-5" /> Constraints</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Risk Tolerance</label>
                <Slider value={[constraints.riskTolerance]} onValueChange={v => setConstraints({ ...constraints, riskTolerance: v[0] })} max={100} step={10} className="mt-2" />
                <div className="flex justify-between text-xs text-gray-500 mt-1"><span>Conservative</span><span>{constraints.riskTolerance}%</span><span>Aggressive</span></div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Distributor Type</label>
                <Select value={constraints.distributorType} onValueChange={v => setConstraints({ ...constraints, distributorType: v })}>
                  <SelectTrigger className="mt-1" data-testid="ca-distributor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="authorized">Authorized Only</SelectItem>
                    <SelectItem value="independent">Include Independent</SelectItem>
                    <SelectItem value="any">Any Source</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Alternate Parts</label>
                <Select value={constraints.alternateType} onValueChange={v => setConstraints({ ...constraints, alternateType: v })}>
                  <SelectTrigger className="mt-1" data-testid="ca-alternate-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">Exact MPN Only</SelectItem>
                    <SelectItem value="fff">Include FFF</SelectItem>
                    <SelectItem value="pin">Include Pin-to-Pin</SelectItem>
                    <SelectItem value="all">All Alternates</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 shadow-sm flex flex-col justify-center">
            <CardContent className="pt-6 text-center">
              <Sparkles className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI Cost Optimizer</h3>
              <p className="text-sm text-gray-600 mb-6">Analyzes 50,000+ data points to find the optimal path</p>
              <Button onClick={runSimulation} disabled={isSimulating || !selectedBom} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white h-12 text-lg disabled:opacity-50" data-testid="run-simulation-btn">
                {isSimulating ? (<><RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Simulating...</>) : (<><Play className="h-5 w-5 mr-2" /> Run AI Simulation</>)}
              </Button>
              {!selectedBom && <p className="text-xs text-gray-500 mt-2">Select a BOM above to enable</p>}
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        {simulationResult && (
          <div className="space-y-6" data-testid="simulation-results">
            {/* Summary Banner + Matched Parts */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className={`md:col-span-2 shadow-sm ${simulationResult.achievable ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-300'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    {simulationResult.achievable ? <CheckCircle className="h-12 w-12 text-green-600 flex-shrink-0" /> : <AlertTriangle className="h-12 w-12 text-yellow-600 flex-shrink-0" />}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{simulationResult.achievable ? 'Target Achievable!' : 'Partial Achievement Possible'}</h3>
                      <p className="text-gray-600">Optimized: <span className="font-bold text-green-700">${simulationResult.optimizedCost.toLocaleString()}</span> | Savings: <span className="font-bold text-green-700">${simulationResult.savings.toLocaleString()}</span></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="matched-parts-card">
                <CardContent className="pt-5">
                  <p className="text-xs text-gray-500 uppercase flex items-center">Matched Parts <InfoTip text="Number of BOM parts matched to our pricing database. Unmatched parts use your uploaded costs and are excluded from optimization." /></p>
                  <p className="text-2xl font-bold text-gray-900">{simulationResult.matchedParts} <span className="text-lg text-gray-400">/ {simulationResult.totalParts}</span></p>
                  <p className="text-xs text-gray-400 mt-1">{simulationResult.totalParts - simulationResult.matchedParts} unmatched</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200 shadow-sm">
                <CardContent className="pt-5">
                  <p className="text-xs text-blue-600 uppercase">Confidence</p>
                  <p className="text-2xl font-bold text-blue-700">{simulationResult.confidence}%</p>
                  <p className="text-xs text-blue-500 mt-1">Based on {simulationResult.matchedParts} matched parts</p>
                </CardContent>
              </Card>
            </div>

            {/* Strategies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-gray-900">Recommended Strategies</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={simulationResult.strategies} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={120} />
                      <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                      <Bar dataKey="impact" radius={[0, 8, 8, 0]}>
                        {simulationResult.strategies.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-gray-900">Strategy Details</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {simulationResult.strategies.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                          <div>
                            <p className="font-medium text-gray-900">{s.name}</p>
                            <p className="text-sm text-gray-500">{s.components} components affected</p>
                          </div>
                        </div>
                        <span className="font-bold text-green-600">${s.impact.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Component Optimization Path — with Best Rate & Negotiation Anchor */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" /> Component Optimization Path
                  <InfoTip text="AI picks the least sensitive/risky parts first, then suggests progressively more impactful changes to reach your target cost. 'Best Rate' is the best verified distributor price. 'Negotiation Anchor' is the weighted median — a realistic negotiation target." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">The AI starts with low-risk adjustments (negotiation, volume) and progressively suggests higher-impact changes (alternates, global sourcing) to reach your target. You can accept or reject each step.</p>
                <div className="space-y-3">
                  {simulationResult.componentPath.map((comp, idx) => {
                    const runningTotal = simulationResult.componentPath.slice(0, idx + 1).reduce((s, c) => s + c.savings, 0);
                    const progress = Math.min((runningTotal / simulationResult.savings) * 100, 100);
                    return (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid={`comp-path-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-white">{idx + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-gray-900 text-sm">{comp.mpn}</span>
                            <Badge className={comp.risk === 'Low' ? 'bg-green-100 text-green-800' : comp.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{comp.risk} sensitivity</Badge>
                          </div>
                          <p className="text-sm text-gray-600">{comp.strategy}</p>
                          {/* Best Rate & Negotiation Anchor */}
                          <div className="flex gap-4 mt-1 text-xs text-gray-500">
                            <span>Best Rate: <span className="font-semibold text-green-700">${comp.bestRate.toFixed(3)}</span></span>
                            <span>Negotiation Anchor: <span className="font-semibold text-blue-700">${comp.negotiationAnchor.toFixed(3)}</span></span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 text-sm">
                          <p className="text-gray-500">${comp.current.toFixed(3)} → <span className="font-bold text-green-700">${comp.optimized.toFixed(3)}</span></p>
                          <p className="font-bold text-green-600">Save ${comp.savings.toLocaleString()}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <QuickActions mpn={comp.mpn} />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{progress.toFixed(0)}% to target</span>
                      </div>
                    </div>
                  );})}
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-800">Total path savings: ${simulationResult.componentPath.reduce((s, c) => s + c.savings, 0).toLocaleString()}</p>
                    <p className="text-xs text-green-700">Accept all low-risk steps to get {((simulationResult.componentPath.filter(c => c.risk === 'Low').reduce((s, c) => s + c.savings, 0) / currentCost) * 100).toFixed(1)}% reduction with minimal risk</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expected Price Trends */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="price-trends-section">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" /> Expected Price Trends
                  <InfoTip text="30-day forward price forecast for each component. Rising trends (red) suggest buying sooner; declining trends (green) suggest holding. Based on historical pricing data and market signals." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {simulationResult.componentPath.map((comp, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50" data-testid={`price-trend-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-semibold text-sm text-gray-900">{comp.mpn}</span>
                        <Badge className={comp.trend > 0 ? 'bg-red-100 text-red-800' : comp.trend < 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {comp.trend > 0 ? '+' : ''}{comp.trend}%
                        </Badge>
                      </div>
                      <ResponsiveContainer width="100%" height={100}>
                        <AreaChart data={comp.trendData}>
                          <Area type="monotone" dataKey="price" stroke={comp.trend > 0 ? '#ef4444' : '#16a34a'} fill={comp.trend > 0 ? '#fee2e2' : '#dcfce7'} strokeWidth={2} />
                          <XAxis dataKey="day" tick={false} />
                          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 9 }} width={35} />
                          <Tooltip formatter={v => `$${Number(v).toFixed(3)}`} />
                        </AreaChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        {comp.trend > 0 ? 'Rising — consider buying now' : comp.trend < 0 ? 'Declining — hold if possible' : 'Stable — no urgency'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Supply Chain Risks Only */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="supply-chain-risks-section">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" /> Supply Chain Risks
                  <InfoTip text="Only supply chain-related risks are shown here — geographic concentration, sole-source dependencies, and lead-time volatility. Other risk types (compliance, quality) are assessed separately." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {simulationResult.supplyChainRisks.map((r, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 border rounded-lg bg-gray-50" data-testid={`sc-risk-${idx}`}>
                      <Badge className={r.level === 'Low' ? 'bg-green-100 text-green-800' : r.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{r.level}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-semibold text-sm text-gray-900">{r.component}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{r.detail}</p>
                        <p className="text-xs text-blue-700 mt-1 font-medium">Mitigation: {r.mitigation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button onClick={exportPDF} variant="outline" className="border-green-300 text-green-700" data-testid="ca-export-pdf">
                <Download className="h-4 w-4 mr-2" /> Export PDF Report
              </Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="ca-apply-recommendations">Apply Recommendations</Button>
            </div>
          </div>
        )}
      </div>
      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </Layout>
  );
};

export default CostArchitect;
